import axios from 'axios';
import { formatNumeroDI } from '../../../../utils/hooks/form/field/formatters';

const baseUrl = process.env.REACT_APP_QCX_API_BASE_URL! + '/movimentacao/report/proforma-packing-list-saida';

export type ProformaPackingListSaidaFiltroOption = {
  id: number;
  value: string;
};

type FiltroOptionResponse = {
  id: number;
  descricao?: string | null;
  processo?: string | null;
};

const montaLabel = (descricao?: string | null, processo?: string | null): string => {
  const partes = [descricao, processo].filter(Boolean);
  return partes.length > 0 ? partes.join(' - ') : '-';
};

const fetchFiltroOptions = async (
  recurso: 'faturas' | 'di-duimps',
  token: string,
  unidadeId: number,
  formatDescricao: (descricao?: string | null) => string | null | undefined
): Promise<ProformaPackingListSaidaFiltroOption[]> => {
  const { data } = await axios.get<FiltroOptionResponse[]>(`${baseUrl}/${recurso}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      quickcomexTenant: unidadeId,
    },
  });

  return (data ?? []).map((option) => ({
    id: option.id,
    value: montaLabel(formatDescricao(option.descricao), option.processo),
  }));
};

const fetchFaturaOptions = (token: string, unidadeId: number) =>
  fetchFiltroOptions('faturas', token, unidadeId, (descricao) => descricao);

// A DI/DUIMP só ganha registro após transmitida; nesse caso o processo identifica sozinho a opção.
const fetchDiDuimpOptions = (token: string, unidadeId: number) =>
  fetchFiltroOptions('di-duimps', token, unidadeId, (registro) => (registro ? formatNumeroDI(registro) : registro));

const generateSpreadsheetProformaPackingListSaida = async (
  token: string,
  unidadeId: number,
  clientesIds: number[] = [],
  exportadoresIds: number[] = [],
  processosNumeros: string[] = [],
  faturasIds: number[] = [],
  diDuimpsIds: number[] = [],
  dataInicio?: string,
  dataFim?: string
) => {
  const params = new URLSearchParams();
  if (clientesIds.length > 0) {
    params.set('clientesId', clientesIds.join(','));
  }
  if (exportadoresIds.length > 0) {
    params.set('exportadoresId', exportadoresIds.join(','));
  }
  processosNumeros.forEach((numero) => {
    params.append('processos', numero);
  });
  if (faturasIds.length > 0) {
    params.set('faturasId', faturasIds.join(','));
  }
  if (diDuimpsIds.length > 0) {
    params.set('diDuimpsId', diDuimpsIds.join(','));
  }
  if (dataInicio) {
    params.set('inicio', dataInicio);
  }
  if (dataFim) {
    params.set('fim', dataFim);
  }

  const query = params.toString();
  const url = query ? `${baseUrl}?${query}` : baseUrl;

  let message = null;

  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
        quickcomexTenant: unidadeId,
      },
      responseType: 'blob',
    });

    if (response.status === 200) {
      const file = new File([response.data], 'relatorioProformaPackingListSaida.xlsx', {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const objectUrl = window.URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = file.name;
      a.click();
    } else {
      message = await response.data.text();
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response && error.response.data) {
        message = await error.response.data.text();
      } else {
        message = 'Erro inesperado';
      }
    } else {
      message = 'Erro inesperado';
    }
  }
  return message;
};

export { generateSpreadsheetProformaPackingListSaida, fetchFaturaOptions, fetchDiDuimpOptions };
